import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useUiLocale } from '../../../app/ui-locale';
import type { FormField, FormValues, SelectOption } from '../../../models/form';
import { getValueByPath } from '../../../utils/conditions';
import { publicCopy } from '../public-copy';

interface Props {
  field: FormField;
  tabName: string;
  values: FormValues;
  value: unknown;
  disabled: boolean;
  controlId: string;
  className: string;
  style?: CSSProperties;
  attributes: Record<string, string | boolean | number>;
  describedBy?: string;
  onChange: (value: unknown) => void;
}

interface LookupOption { value: string; label: string; disabled?: boolean }

function staticOptions(field: FormField): LookupOption[] {
  return (field.options ?? []).map((option) => ({ value: option.value, label: option.label, disabled: option.disabled }));
}

function safeRemoteUrl(path: string | undefined): URL | undefined {
  const value = path?.trim();
  if (!value || !value.startsWith('/') || value.startsWith('//') || value.includes('\\')) return undefined;
  const url = new URL(value, window.location.origin);
  return url.origin === window.location.origin ? url : undefined;
}

function mapRemoteOptions(field: FormField, payload: unknown): LookupOption[] {
  const root = payload as { items?: unknown } | undefined;
  const source = Array.isArray(payload) ? payload : Array.isArray(root?.items) ? root?.items : [];
  const valueField = field.lookupValueField?.trim() || 'value';
  const labelField = field.lookupLabelField?.trim() || 'label';
  return source.slice(0, 100).map((item) => {
    const record = item as Record<string, unknown>;
    return { value: String(record?.[valueField] ?? ''), label: String(record?.[labelField] ?? record?.[valueField] ?? ''), disabled: Boolean(record?.disabled) };
  }).filter((item) => item.value && item.label);
}

export function LookupInput({ field, tabName, values, value, disabled, controlId, className, style, attributes, describedBy, onChange }: Props) {
  const { locale, culture } = useUiLocale();
  const copy = publicCopy(locale);
  const initialStatic = useMemo(() => staticOptions(field), [field.options]);
  const currentValue = String(value ?? '');
  const currentStaticLabel = initialStatic.find((option) => option.value === currentValue)?.label;
  const [term, setTerm] = useState(currentStaticLabel ?? currentValue);
  const [options, setOptions] = useState<LookupOption[]>(initialStatic);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const requestRef = useRef<AbortController | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const minChars = Math.max(0, Math.min(10, field.lookupMinChars ?? 1));
  const dependencyValue = field.lookupDependsOn ? getValueByPath(values, field.lookupDependsOn) : undefined;

  useEffect(() => {
    const label = initialStatic.find((option) => option.value === currentValue)?.label;
    if (label) setTerm(label);
    else if (!currentValue) setTerm('');
  }, [currentValue, initialStatic]);

  useEffect(() => {
    const listener = (event: MouseEvent) => {
      if (!wrapperRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', listener);
    return () => document.removeEventListener('mousedown', listener);
  }, []);

  useEffect(() => {
    if (!field.lookupEnabled || field.lookupMode !== 'remote') {
      const needle = term.trim().toLocaleLowerCase(culture);
      setOptions(!needle ? initialStatic : initialStatic.filter((option) => `${option.label} ${option.value}`.toLocaleLowerCase(culture).includes(needle)));
      setError('');
      return;
    }
    if (term.trim().length < minChars) {
      setOptions([]);
      setError('');
      return;
    }
    const base = safeRemoteUrl(field.lookupUrl);
    if (!base) {
      setOptions([]);
      setError(copy.apiSameSite);
      return;
    }
    requestRef.current?.abort();
    const controller = new AbortController();
    requestRef.current = controller;
    const timer = window.setTimeout(async () => {
      setLoading(true);
      setError('');
      try {
        base.searchParams.set('q', term.trim());
        if (field.lookupDependsOn) base.searchParams.set('dependsOn', field.lookupDependsOn);
        if (dependencyValue != null && String(dependencyValue).trim()) base.searchParams.set('dependsValue', String(dependencyValue));
        const response = await fetch(base.toString(), { credentials: 'same-origin', headers: { Accept: 'application/json' }, signal: controller.signal });
        if (!response.ok) throw new Error(copy.optionLoadError(response.status));
        const nextOptions = mapRemoteOptions(field, await response.json());
        setOptions(nextOptions);
        const selected = nextOptions.find((option) => option.value === currentValue);
        if (selected && term.trim() === currentValue) setTerm(selected.label);
      } catch (requestError) {
        if ((requestError as Error).name !== 'AbortError') {
          setOptions([]);
          setError(requestError instanceof Error ? requestError.message : copy.optionLoadFailed);
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }, 250);
    return () => { window.clearTimeout(timer); controller.abort(); };
  }, [copy, culture, currentValue, dependencyValue, field, initialStatic, minChars, term]);

  const choose = (option: LookupOption) => {
    if (option.disabled) return;
    setTerm(option.label);
    onChange(option.value);
    setOpen(false);
  };

  return <div ref={wrapperRef} className="df-lookup-control">
    <div className="df-lookup-input-wrap">
      <input
        {...attributes}
        id={controlId}
        name={`${field.name}-lookup`}
        className={className}
        style={style}
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls={`${controlId}-options`}
        aria-describedby={describedBy}
        autoComplete="off"
        value={term}
        placeholder={field.placeholder || copy.lookupPlaceholder}
        disabled={disabled}
        onFocus={() => setOpen(true)}
        onChange={(event) => {
          setTerm(event.target.value);
          setOpen(true);
          if (!event.target.value) onChange('');
        }}
        onKeyDown={(event) => {
          if (event.key === 'Escape') setOpen(false);
          if (event.key === 'Enter' && options.length === 1 && options[0] && !options[0].disabled) { event.preventDefault(); choose(options[0]); }
        }}
      />
      {currentValue ? <button type="button" className="df-lookup-clear" disabled={disabled} aria-label={copy.clearSelection} onClick={() => { setTerm(''); onChange(''); setOpen(false); }}>×</button> : <span className="df-lookup-search" aria-hidden="true">⌕</span>}
    </div>
    {open && !disabled ? <div id={`${controlId}-options`} className="df-lookup-options" role="listbox">
      {loading ? <div className="df-lookup-state">{copy.searching}</div> : null}
      {!loading && error ? <div className="df-lookup-state text-danger">{error}</div> : null}
      {!loading && !error && term.trim().length < minChars ? <div className="df-lookup-state">{copy.minimumCharacters(minChars)}</div> : null}
      {!loading && !error && term.trim().length >= minChars && !options.length ? <div className="df-lookup-state">{copy.noOptions}</div> : null}
      {!loading && !error ? options.map((option) => <button type="button" role="option" aria-selected={option.value === currentValue} disabled={option.disabled} key={`${option.value}-${option.label}`} onMouseDown={(event) => event.preventDefault()} onClick={() => choose(option)}><strong>{option.label}</strong>{option.value !== option.label ? <small>{option.value}</small> : null}</button>) : null}
    </div> : null}
  </div>;
}
